import { getUserProfile } from '@/api/user';
import { subscribeAuthState } from '@/utils/auth-session';
import { getAuthGeneration, registerAuthTeardown } from '@/utils/auth-teardown';
import { getOriginLoginType, getWalletAddress, hasAuthSession } from '@/utils/auth-token';
import { extractPrivyEmail, getPrivySessionUser, isPrivyEmbeddedLogin } from '@/utils/privy';

/** 首次注入前最多等资料这么久，避免挡住预载/打开客服 */
const FIRST_LOAD_SYNC_MS = 2500;
const WALLET_EMAIL_DOMAIN = 'wallet.deepsage.app';

/** @type {{ name?: string, email?: string } | null} */
let cachedVisitor = null;
/** @type {Promise<{ name?: string, email?: string } | null> | null} */
let inflightSync = null;
/** @type {Promise<void> | null} */
let firstLoadSync = null;

function walletAddressToVisitorEmail(address) {
  const local = String(address || '')
    .trim()
    .replace(/[^a-zA-Z0-9._+-]/g, '')
    .slice(0, 64);
  return local ? `${local}@${WALLET_EMAIL_DOMAIN}` : '';
}

function getTawkApi() {
  if (typeof window === 'undefined') return null;
  return window.Tawk_API || (window.Tawk_API = {});
}

function writeVisitorObject(visitor) {
  const api = getTawkApi();
  if (!api) return;
  if (visitor && (visitor.name || visitor.email)) {
    api.visitor = { ...visitor };
    return;
  }
  if (!api.visitor) return;
  try {
    delete api.visitor;
  } catch {
    api.visitor = undefined;
  }
}

function setAttributesIfReady(visitor) {
  if (typeof window === 'undefined' || !window.__dsTawkWidgetReady) return;
  const api = window.Tawk_API;
  if (!api || typeof api.setAttributes !== 'function' || !visitor) return;
  if (!visitor.name && !visitor.email) return;
  try {
    api.setAttributes({ ...visitor }, () => {});
  } catch {
    /* ignore */
  }
}

/**
 * 把已缓存的姓名/邮箱写到 Tawk_API.visitor（须在 embed 脚本下载前调用才对首屏生效）。
 */
export function applyCachedTawkVisitor() {
  if (cachedVisitor) writeVisitorObject(cachedVisitor);
}

function applyVisitor(visitor) {
  cachedVisitor = visitor && (visitor.name || visitor.email) ? { ...visitor } : null;
  writeVisitorObject(cachedVisitor);
  if (cachedVisitor) setAttributesIfReady(cachedVisitor);
}

async function resolveVisitor() {
  if (!hasAuthSession()) return null;
  const generation = getAuthGeneration();

  let profile = null;
  try {
    profile = await getUserProfile();
  } catch (err) {
    console.warn('[tawk] getUserProfile failed', err);
  }
  if (generation !== getAuthGeneration() || !hasAuthSession()) return null;

  let name = String(profile?.nickName || profile?.username || '').trim();
  let email = '';

  if (getOriginLoginType() === 'WALLET') {
    const walletAddress = String(getWalletAddress() || '').trim();
    if (!name) name = walletAddress;
    email = walletAddressToVisitorEmail(walletAddress);
  }
  if (!email) email = String(profile?.email || '').trim();

  if (!email && isPrivyEmbeddedLogin()) {
    try {
      email = extractPrivyEmail(await getPrivySessionUser());
    } catch (err) {
      console.warn('[tawk] privy email failed', err);
    }
  }
  if (generation !== getAuthGeneration() || !hasAuthSession()) return null;
  if (!name && !email) return null;

  /** @type {{ name?: string, email?: string }} */
  const visitor = {};
  if (name) visitor.name = name;
  if (email) visitor.email = email;
  return visitor;
}

/**
 * 把当前登录用户的姓名/邮箱同步给 Tawk。
 * Widget 未加载时只写 visitor；已加载则再 setAttributes（SPA 登录后）。
 * @returns {Promise<{ name?: string, email?: string } | null>}
 */
export function syncTawkVisitor() {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (!hasAuthSession()) {
    applyVisitor(null);
    return Promise.resolve(null);
  }
  if (inflightSync) return inflightSync;
  inflightSync = resolveVisitor()
    .then((visitor) => {
      if (!hasAuthSession()) {
        applyVisitor(null);
        return null;
      }
      applyVisitor(visitor);
      return visitor;
    })
    .finally(() => {
      inflightSync = null;
    });
  return inflightSync;
}

/**
 * 首次注入脚本前等待一次身份同步（有超时），避免 visitor 设不上。
 * @returns {Promise<void>}
 */
export function ensureTawkVisitorBeforeFirstLoad() {
  if (firstLoadSync) return firstLoadSync;
  const sync = syncTawkVisitor().then(() => {});
  if (typeof window === 'undefined') {
    firstLoadSync = sync.catch(() => {});
    return firstLoadSync;
  }
  firstLoadSync = Promise.race([
    sync,
    new Promise((resolve) => {
      window.setTimeout(resolve, FIRST_LOAD_SYNC_MS);
    }),
  ]).catch(() => {});
  return firstLoadSync;
}

export function clearTawkVisitor() {
  cachedVisitor = null;
  if (typeof window === 'undefined') return;
  writeVisitorObject(null);
  if (!window.__dsTawkWidgetReady) return;
  const api = window.Tawk_API;
  if (!api || typeof api.logout !== 'function') return;
  try {
    api.logout(() => {});
  } catch {
    /* ignore */
  }
}

if (typeof window !== 'undefined') {
  window.dsTawkSyncVisitor = function dsTawkSyncVisitor() {
    if (cachedVisitor) {
      writeVisitorObject(cachedVisitor);
      setAttributesIfReady(cachedVisitor);
      return;
    }
    if (hasAuthSession()) void syncTawkVisitor();
  };
}

subscribeAuthState((loggedIn) => {
  if (loggedIn) {
    void syncTawkVisitor();
    return;
  }
  clearTawkVisitor();
});

registerAuthTeardown(clearTawkVisitor);
